import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Trash2, Plus, Save, MousePointer2, Type } from 'lucide-react';
import { API, authHeaders, apiErr } from './adminApi';

/**
 * Drag-and-drop designer for overlay-mode letterhead templates.
 *
 * Loads a letterhead's image + existing fields, lets the admin drag/resize fields
 * onto the image (coordinates stored as % of image dimensions), and persists via
 * PATCH /api/letterheads/{id}.
 *
 * Sample data preview: the rendered text uses placeholder values so admins can
 * eyeball alignment without needing a real estimate.
 */

// Catalog of injectable fields — keep in sync with backend models.OverlayField + EstimateView/InvoiceView renderers
const FIELD_CATALOG = [
  { key: 'text',             label: 'Custom text (prefilled)', sample: 'Click to edit text…', allowMany: true },
  { key: 'customer_name',    label: 'Customer Name',    sample: 'Jane Doe' },
  { key: 'customer_email',   label: 'Customer Email',   sample: 'jane@example.com' },
  { key: 'customer_phone',   label: 'Customer Phone',   sample: '(555) 555-1234' },
  { key: 'customer_address', label: 'Customer Address', sample: '123 Pine St, Moncks Corner, SC' },
  { key: 'document_number',  label: 'Document Number',  sample: 'EST-1042' },
  { key: 'document_date',    label: 'Date',             sample: 'February 22, 2026' },
  { key: 'due_date',         label: 'Due Date',         sample: 'March 8, 2026' },
  { key: 'valid_until',      label: 'Valid Until',      sample: 'March 22, 2026' },
  { key: 'subtotal',         label: 'Subtotal',         sample: '$1,250.00' },
  { key: 'tax',              label: 'Tax',              sample: '$87.50' },
  { key: 'total',            label: 'Total',            sample: '$1,337.50' },
  { key: 'paid',             label: 'Paid to date',     sample: '$500.00' },
  { key: 'balance_due',      label: 'Balance Due',      sample: '$837.50' },
  { key: 'notes',            label: 'Notes',            sample: 'Deposit due at drop-off. Estimated 2 weeks.' },
  { key: 'line_items',       label: 'Line Items (table)', sample: '(rendered as a table of services)' },
];

const FIELD_META = FIELD_CATALOG.reduce((acc, f) => { acc[f.key] = f; return acc; }, {});

const DEFAULT_FIELD = {
  x: 5, y: 5, width: 30, height: 5, font_size: 14, font_color: '#0f172a', align: 'left', bold: false,
};

const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), hi);

const OverlayDesigner = ({ open, onClose, letterheadId, onSaved }) => {
  const [letterhead, setLetterhead] = useState(null);
  const [fields, setFields] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [dragState, setDragState] = useState(null); // { mode: 'move'|'resize', idx, startX, startY, init: field }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);

  // Load the letterhead with image + existing fields
  useEffect(() => {
    if (!open || !letterheadId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/letterheads/${letterheadId}`, { headers: authHeaders() });
        if (cancelled) return;
        setLetterhead(data);
        setFields(Array.isArray(data.fields) ? data.fields : []);
        setSelectedIdx(-1);
      } catch (err) {
        toast.error(`Load failed: ${apiErr(err)}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, letterheadId]);

  // ----- Mouse interaction: drag to move, drag a corner handle to resize -----
  const onCanvasMouseDown = (e, idx, mode) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedIdx(idx);
    const rect = canvasRef.current.getBoundingClientRect();
    setDragState({
      mode,
      idx,
      startX: e.clientX,
      startY: e.clientY,
      canvasW: rect.width,
      canvasH: rect.height,
      init: { ...fields[idx] },
    });
  };

  useEffect(() => {
    if (!dragState) return undefined;
    const onMove = (e) => {
      const dxPct = ((e.clientX - dragState.startX) / dragState.canvasW) * 100;
      const dyPct = ((e.clientY - dragState.startY) / dragState.canvasH) * 100;
      setFields((prev) => {
        const next = [...prev];
        const f = { ...dragState.init };
        if (dragState.mode === 'move') {
          f.x = clamp(f.x + dxPct, 0, 100 - f.width);
          f.y = clamp(f.y + dyPct, 0, 100 - f.height);
        } else if (dragState.mode === 'resize') {
          f.width = clamp(f.width + dxPct, 4, 100 - f.x);
          f.height = clamp(f.height + dyPct, 2, 100 - f.y);
        }
        next[dragState.idx] = f;
        return next;
      });
    };
    const onUp = () => setDragState(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragState]);

  // ----- Field management -----
  const addField = (key) => {
    if (!key) return;
    const meta = FIELD_META[key];
    if (!meta?.allowMany && fields.some((f) => f.key === key)) {
      toast.info(`Field "${meta?.label || key}" already placed — drag to reposition.`);
      return;
    }
    const initial = { ...DEFAULT_FIELD, key };
    if (key === 'line_items') { initial.width = 80; initial.height = 30; initial.y = 40; }
    if (key === 'notes') { initial.width = 80; initial.height = 12; }
    if (key === 'text') {
      // Stagger Y for each new text field so they don't pile up at the same spot
      const existingTextCount = fields.filter((f) => f.key === 'text').length;
      initial.width = 40;
      initial.height = 6;
      initial.y = Math.min(30 + existingTextCount * 8, 90);
      initial.default_text = '';
    }
    setFields((prev) => [...prev, initial]);
    setSelectedIdx(fields.length);
  };

  const updateSelected = (patch) => {
    setFields((prev) => prev.map((f, i) => (i === selectedIdx ? { ...f, ...patch } : f)));
  };

  const removeField = (idx) => {
    setFields((prev) => prev.filter((_, i) => i !== idx));
    setSelectedIdx(-1);
  };

  const save = useCallback(async () => {
    if (!letterhead) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API}/api/letterheads/${letterhead.id}`,
        { template_mode: 'overlay', fields },
        { headers: authHeaders() },
      );
      toast.success(`Saved ${fields.length} field${fields.length === 1 ? '' : 's'}`);
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(`Save failed: ${apiErr(err)}`);
    } finally {
      setSaving(false);
    }
  }, [letterhead, fields, onClose, onSaved]);

  const selected = selectedIdx >= 0 ? fields[selectedIdx] : null;
  const unusedFields = useMemo(
    () => FIELD_CATALOG.filter((f) => f.allowMany || !fields.some((p) => p.key === f.key)),
    [fields],
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-6xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="overlay-designer-title" className="flex items-center gap-2">
            <MousePointer2 className="w-5 h-5 text-amber-400" />
            Template Designer — {letterhead?.name || '…'}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Click <span className="text-amber-300">Add field</span>, then drag the box onto your form. Drag the bottom-right corner to resize.
            When a customer views the estimate or invoice, your data is painted directly onto the form.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-gray-400" data-testid="overlay-designer-loading">Loading template…</p>
        ) : letterhead ? (
          <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-4 mt-2">
            {/* ===== Canvas with the image + draggable boxes ===== */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Select value="" onValueChange={(v) => { addField(v); }}>
                  <SelectTrigger data-testid="overlay-add-field" className="bg-slate-800 border-slate-700 text-white w-56">
                    <SelectValue placeholder={`Add field (${unusedFields.length} available)`} />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 text-white">
                    {unusedFields.length === 0 ? (
                      <SelectItem value="__none" disabled>All fields placed</SelectItem>
                    ) : unusedFields.map((f) => (
                      <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs text-gray-500">{fields.length} field{fields.length === 1 ? '' : 's'} placed</span>
                <div className="flex-1" />
                <Button data-testid="overlay-save" onClick={save} disabled={saving} size="sm" className="bg-red-600 hover:bg-red-700 text-white">
                  <Save className="w-3 h-3 mr-2" /> {saving ? 'Saving…' : 'Save template'}
                </Button>
              </div>

              <div
                ref={canvasRef}
                data-testid="overlay-canvas"
                className="relative w-full bg-white rounded border border-slate-700 overflow-hidden select-none"
                style={{ aspectRatio: '8.5 / 11' }}
                onMouseDown={() => setSelectedIdx(-1)}
              >
                <img
                  src={`data:image/png;base64,${letterhead.image_base64}`}
                  alt="Template"
                  className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                />
                {fields.map((f, idx) => {
                  const meta = FIELD_META[f.key] || { label: f.key, sample: '' };
                  const isSelected = idx === selectedIdx;
                  const isText = f.key === 'text';
                  const displayContent = isText
                    ? (f.default_text || '(empty text — click to edit)')
                    : meta.sample;
                  return (
                    <div
                      key={`${f.key}-${idx}`}
                      data-testid={`overlay-field-${f.key}`}
                      onMouseDown={(e) => onCanvasMouseDown(e, idx, 'move')}
                      style={{
                        position: 'absolute',
                        left: `${f.x}%`,
                        top: `${f.y}%`,
                        width: `${f.width}%`,
                        height: `${f.height}%`,
                        cursor: 'move',
                        fontSize: `${f.font_size}px`,
                        color: f.font_color,
                        fontWeight: f.bold ? 700 : 400,
                        textAlign: f.align,
                        whiteSpace: isText ? 'pre-wrap' : 'normal',
                      }}
                      className={`flex items-start px-1 py-0.5 leading-tight overflow-hidden ${
                        isSelected ? 'ring-2 ring-amber-400 ring-offset-1 bg-amber-50/30' : 'ring-1 ring-blue-400/40 bg-blue-50/15 hover:bg-amber-50/20'
                      }`}
                    >
                      <span className="block w-full pointer-events-none">
                        {isText
                          ? <span className={f.default_text ? '' : 'italic opacity-60'}>{displayContent}</span>
                          : <><span className="text-[9px] uppercase tracking-wider opacity-60 mr-1">{meta.label}:</span>{displayContent}</>}
                      </span>
                      {/* Resize handle */}
                      <span
                        data-testid={`overlay-resize-${f.key}`}
                        onMouseDown={(e) => onCanvasMouseDown(e, idx, 'resize')}
                        className="absolute bottom-0 right-0 w-3 h-3 bg-amber-400 cursor-nwse-resize border border-amber-700"
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ===== Side panel: edit selected field ===== */}
            <div className="rounded border border-slate-800 bg-slate-950/40 p-3 space-y-3 text-xs h-fit">
              {selected ? (
                <>
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-amber-300 flex items-center gap-1.5">
                      {selected.key === 'text' && <Type className="w-3.5 h-3.5" />}
                      {FIELD_META[selected.key]?.label || selected.key}
                    </p>
                    <Button
                      size="sm" variant="outline"
                      data-testid="overlay-remove-field"
                      onClick={() => removeField(selectedIdx)}
                      className="border-red-900/60 text-red-400 hover:bg-red-950/40 px-2 h-7"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>

                  {selected.key === 'text' && (
                    <div>
                      <Label className="text-gray-400 text-[10px]">Text content (prefilled, shown on every document)</Label>
                      <Textarea
                        data-testid="overlay-text-content"
                        value={selected.default_text || ''}
                        onChange={(e) => updateSelected({ default_text: e.target.value })}
                        rows={4}
                        maxLength={500}
                        placeholder="e.g. Terms & Conditions, disclaimer, custom heading…"
                        className="bg-slate-800 border-slate-700 text-white text-xs"
                      />
                      <p className="text-[10px] text-gray-500 mt-0.5">{(selected.default_text || '').length}/500</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-gray-400 text-[10px]">X (%)</Label>
                      <Input type="number" min="0" max="100" step="0.5" value={selected.x.toFixed(1)}
                        onChange={(e) => updateSelected({ x: clamp(parseFloat(e.target.value) || 0, 0, 100) })}
                        className="bg-slate-800 border-slate-700 text-white h-7 text-xs" />
                    </div>
                    <div>
                      <Label className="text-gray-400 text-[10px]">Y (%)</Label>
                      <Input type="number" min="0" max="100" step="0.5" value={selected.y.toFixed(1)}
                        onChange={(e) => updateSelected({ y: clamp(parseFloat(e.target.value) || 0, 0, 100) })}
                        className="bg-slate-800 border-slate-700 text-white h-7 text-xs" />
                    </div>
                    <div>
                      <Label className="text-gray-400 text-[10px]">Width (%)</Label>
                      <Input type="number" min="4" max="100" step="0.5" value={selected.width.toFixed(1)}
                        onChange={(e) => updateSelected({ width: clamp(parseFloat(e.target.value) || 4, 4, 100) })}
                        className="bg-slate-800 border-slate-700 text-white h-7 text-xs" />
                    </div>
                    <div>
                      <Label className="text-gray-400 text-[10px]">Height (%)</Label>
                      <Input type="number" min="2" max="100" step="0.5" value={selected.height.toFixed(1)}
                        onChange={(e) => updateSelected({ height: clamp(parseFloat(e.target.value) || 2, 2, 100) })}
                        className="bg-slate-800 border-slate-700 text-white h-7 text-xs" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-400 text-[10px]">Font size (px)</Label>
                    <Input type="number" min="8" max="48" value={selected.font_size}
                      onChange={(e) => updateSelected({ font_size: clamp(parseInt(e.target.value, 10) || 12, 8, 48) })}
                      className="bg-slate-800 border-slate-700 text-white h-7 text-xs" />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-[10px]">Color</Label>
                    <Input type="color" value={selected.font_color}
                      onChange={(e) => updateSelected({ font_color: e.target.value })}
                      className="bg-slate-800 border-slate-700 h-7 w-full p-0 cursor-pointer" />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-[10px]">Align</Label>
                    <Select value={selected.align} onValueChange={(v) => updateSelected({ align: v })}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700 text-white">
                        <SelectItem value="left">Left</SelectItem>
                        <SelectItem value="center">Center</SelectItem>
                        <SelectItem value="right">Right</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={selected.bold}
                      onChange={(e) => updateSelected({ bold: e.target.checked })} />
                    <span className="text-gray-300">Bold</span>
                  </label>
                </>
              ) : (
                <div className="text-gray-500 text-center py-4">
                  <Plus className="w-5 h-5 mx-auto mb-2 opacity-50" />
                  <p>Click a field on the form to edit it, or use <span className="text-amber-300">Add field</span> to place a new one.</p>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default OverlayDesigner;
